/**
 * NCB 核心服務 (直接連接版本)
 */

const NCB_INSTANCE = import.meta.env.VITE_NCB_INSTANCE || '55538_midnight_bar';
const NCB_AUTH_API_URL = import.meta.env.VITE_NCB_AUTH_API_URL || 'https://app.nocodebackend.com/api/user-auth';
const NCB_DATA_API_URL = import.meta.env.VITE_NCB_DATA_API_URL || 'https://app.nocodebackend.com/api/data';
const TOKEN_KEY = 'midnight_bar_token';

// 🔧 修復:加入 Bearer Token 支援,避免瀏覽器阻擋第三方 Cookie 導致登出
const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Database-Instance': NCB_INSTANCE,
  };
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const getAuthUrl = (path: string): string => {
  return `${NCB_AUTH_API_URL}/${path}?Instance=${NCB_INSTANCE}`;
};

const getDataUrl = (path: string): string => {
  return `${NCB_DATA_API_URL}/${path}?Instance=${NCB_INSTANCE}`;
};

// 儲存 Token
const saveToken = (data: any) => {
  const token = data?.token || data?.session?.token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

export const ncbService = {
  //==========================================
  // Phase 1: Authentication API
  //==========================================

  async getProviders() {
    try {
      const res = await fetch(getAuthUrl('providers'), {
        headers: getHeaders(),
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      return res.json();
    } catch (error) {
      return { providers: { email: true, google: true } };
    }
  },

  async register(email: string, password: string, name?: string) {
    const res = await fetch(getAuthUrl('sign-up/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password, name: name || '旅人' })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || '註冊失敗');
    }
    
    saveToken(data);
    return data;
  },

  async login(email: string, password: string) {
    const res = await fetch(getAuthUrl('sign-in/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || '登入失敗,請檢查帳號密碼');
    }

    saveToken(data);
    return data;
  },

  async logout() {
    try {
      await fetch(getAuthUrl('sign-out'), {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: '{}'
      });
    } catch (e) {
      console.error('Logout failed', e);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
    }
  },

  async getSession() {
    try {
      const res = await fetch(getAuthUrl('get-session'), {
        headers: getHeaders(),
        credentials: 'include'
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
        }
        return { user: null };
      }

      return res.json();
    } catch (e) {
      return { user: null };
    }
  },

  getGoogleAuthUrl(callbackUrl: string) {
    return `${NCB_AUTH_API_URL}/sign-in/social?provider=google&callbackURL=${encodeURIComponent(callbackUrl)}&Instance=${NCB_INSTANCE}`;
  },

  //==========================================
  // Phase 2: Data API
  //==========================================

  async dataRead(table: string, query: string = '') {
    const url = getDataUrl(`read/${table}${query ? '?' + query : ''}`);
    const res = await fetch(url, { headers: getHeaders(), credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataCreate(table: string, payload: any) {
    const res = await fetch(getDataUrl(`create/${table}`), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataUpdate(table: string, id: string, payload: any) {
    const res = await fetch(getDataUrl(`update/${table}/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataDelete(table: string, id: string) {
    const res = await fetch(getDataUrl(`delete/${table}/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  }
};